// bootProgress — what the door's percentage is made of (2026-10-31).
//
// Khari: "it loads quickly to 99 and freezes on 99 without visual update until
// it is done." The Loader showed three's loading manager — files finished over
// files asked for — and the boot asks in WAVES (the city, then its textures,
// then the people by the spawn), so the first wave finishing read as 99 and the
// rest of the wait sat under it. The number now comes from the boot itself:
//   bytes  the city and skyline GLBs as they actually arrive (their warm-up
//          fetches stream through `warmAndTrack`, so the count is real bytes)
//   marks  the steps after the download, each reported by the code that does
//          it: 'city:parsed' (CityModel has its model), 'city:batched' (the
//          city merged into draw batches), 'frame' (frames drawn with it)
// Plain module state with a subscribe, read by the Loader through
// useSyncExternalStore; nothing in the 3D re-renders for it.

import { DefaultLoadingManager } from 'three';

const state = { bytes: {}, marks: new Set(), v: 0 };

/* WHAT THE DOOR IS WAITING ON, BY NAME (2026-10-31). The gate opens after
   three's loading manager has been quiet for a moment; on a cold boot it never
   went quiet and the door opened only on its 30-second safety timer — the "30
   second" first load. Its own counters say how many, never which, so the
   manager's item calls are wrapped once here to keep the set of URLs in flight
   (`pendingLoads`), which the Loader names in the console when it waits long. */
const pending = new Map();   // url → started at
{
  const m = DefaultLoadingManager;
  if (!m.__tracked) {
    m.__tracked = true;
    const start = m.itemStart.bind(m); const end = m.itemEnd.bind(m); const err = m.itemError.bind(m);
    m.itemStart = (url) => { pending.set(url, Date.now()); start(url); };
    m.itemEnd = (url) => { pending.delete(url); end(url); };
    m.itemError = (url) => { pending.delete(url); err(url); };
  }
}
/** The loads in flight now: [{ url, ms }] oldest first. */
export function pendingLoads() {
  const now = Date.now();
  return [...pending].map(([url, t]) => ({ url, ms: now - t })).sort((a, b) => b.ms - a.ms);
}
const listeners = new Set();
let queued = false;

function notify() {
  state.v += 1;
  if (queued) return;
  queued = true;
  // One update a frame at most: a 5 MB download arrives in ~80 chunks.
  const flush = () => { queued = false; for (const fn of listeners) fn(); };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(flush); else setTimeout(flush, 16);
}

export function subscribeBoot(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function bootVersion() { return state.v; }
export function bootState() { return state; }

/** A boot step done (once). */
export function bootMark(name) {
  if (state.marks.has(name)) return;
  state.marks.add(name);
  notify();
}

/**
 * Warm the HTTP cache with `url` (the in-component loader then parses it from
 * memory) and count its bytes as they arrive under `key`. The body is READ to
 * the end either way — an unconsumed response is not reliably cached.
 */
export function warmAndTrack(key, url) {
  const set = (got, total) => { state.bytes[key] = [Math.min(got, total), total]; notify(); };
  return fetch(url).then(async (r) => {
    const total = Number(r.headers.get('content-length')) || 0;
    if (!r.body?.getReader || !total) { await r.arrayBuffer(); set(1, 1); return; }
    const reader = r.body.getReader();
    let got = 0;
    set(0, total);
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      got += value.length;
      set(got, total);
    }
    set(total, total);
  }).catch(() => set(1, 1));   // the real load reports failures; the count just stops waiting on it
}

/**
 * The door's estimate, 3..97 (the Loader adds 99 for "settling" and 100 for
 * ready): the download carries it to 70, each step after it to its own floor.
 */
export function bootEstimate() {
  let got = 0; let total = 0;
  for (const [g, t] of Object.values(state.bytes)) { got += g; total += t; }
  const dl = total ? got / total : 0;
  let est = 3 + dl * 67;
  if (state.marks.has('city:parsed')) est = Math.max(est, 78);
  if (state.marks.has('city:batched')) est = Math.max(est, 86);
  if (state.marks.has('frame')) est = Math.max(est, 93);
  return Math.round(est);
}

/** The heavy download is done (the bandwidth is free for anything decorative). */
export function bootDownloaded() {
  const v = Object.values(state.bytes);
  return state.marks.has('city:parsed') || (v.length > 0 && v.every(([g, t]) => g >= t));
}
